import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { ChevronDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button'
import { GradientWash } from '@/components/generals/gradient-wash'
import { mainNav, platformProducts } from '@/constants/navigation'

interface MobileMenuProps {
    open: boolean
    onClose: () => void
}

/** Full-screen navigation sheet for small screens. Platform expands as an accordion. */
export const MobileMenu: FC<MobileMenuProps> = ({ open, onClose }): ReactNode => {
    const [platformOpen, setPlatformOpen] = useState(false)
    const closeRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (!open) return

        const previous = document.body.style.overflow
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }

        document.body.style.overflow = 'hidden'
        document.addEventListener('keydown', onKeyDown)
        closeRef.current?.focus()

        return () => {
            document.body.style.overflow = previous
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [open, onClose])

    return (
        <div
            id='mobile-menu'
            role='dialog'
            aria-modal='true'
            aria-label='Menu'
            inert={!open}
            className={cn(
                'fixed inset-0 z-[60] isolate flex flex-col bg-cream transition-[opacity,visibility] duration-300 ease-calm lg:hidden',
                open ? 'visible opacity-100' : 'invisible opacity-0'
            )}
        >
            <GradientWash variant='soft' />

            <div className='mx-3 mt-3 flex h-14 items-center justify-between pl-4 pr-2 sm:mx-4 sm:mt-4'>
                <Link href='/' onClick={onClose} aria-label='Quayside home' className='rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine'>
                    <Logo />
                </Link>

                <button
                    ref={closeRef}
                    type='button'
                    onClick={onClose}
                    aria-label='Close menu'
                    className='grid size-11 place-items-center rounded-full border border-ink/15 bg-paper/70 text-ink transition-colors hover:bg-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine'
                >
                    <X aria-hidden='true' className='size-5' />
                </button>
            </div>

            <nav aria-label='Mobile' className='flex-1 overflow-y-auto px-5 pb-8 pt-6 sm:px-8'>
                <ul className='divide-y divide-ink/10 border-y border-ink/10'>
                    <li>
                        <button
                            type='button'
                            aria-expanded={platformOpen}
                            aria-controls='mobile-platform'
                            onClick={() => setPlatformOpen(value => !value)}
                            className='flex w-full items-center justify-between py-5 text-left font-heading text-[32px] font-semibold tracking-[-0.035em] text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine'
                        >
                            Platform
                            <ChevronDown aria-hidden='true' className={cn('size-6 text-stone transition-transform duration-300', platformOpen && 'rotate-180')} />
                        </button>

                        <div
                            id='mobile-platform'
                            className={cn('grid transition-[grid-template-rows] duration-500 ease-calm', platformOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}
                        >
                            <div className='overflow-hidden' inert={!platformOpen}>
                                <ul className='space-y-1 pb-5'>
                                    {platformProducts.map(product => {
                                        const Icon = product.icon

                                        return (
                                            <li key={product.id}>
                                                <Link
                                                    href={product.href}
                                                    onClick={onClose}
                                                    className='flex items-center gap-4 rounded-2xl p-3 transition-colors hover:bg-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine'
                                                >
                                                    <span className='grid size-11 shrink-0 place-items-center rounded-xl border border-ink/10 bg-paper text-ink'>
                                                        <Icon aria-hidden='true' className='size-5' strokeWidth={1.6} />
                                                    </span>

                                                    <span>
                                                        <span className='block text-[15px] font-semibold tracking-tight text-ink'>
                                                            {product.name}
                                                        </span>

                                                        <span className='mt-0.5 block text-[13px] leading-snug text-stone'>
                                                            {product.description}
                                                        </span>
                                                    </span>
                                                </Link>
                                            </li>
                                        )
                                    })}
                                </ul>
                            </div>
                        </div>
                    </li>

                    {mainNav.map(link => (
                        <li key={link.href}>
                            <Link
                                href={link.href}
                                onClick={onClose}
                                className='block py-5 font-heading text-[32px] font-semibold tracking-[-0.035em] text-ink transition-colors hover:text-ultramarine focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine'
                            >
                                {link.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            </nav>

            <div className='grid gap-3 border-t border-ink/10 px-5 py-5 sm:px-8'>
                <ButtonLink href='/contact' size='lg' className='w-full' onClick={onClose}>
                    Book a walkthrough
                </ButtonLink>

                <ButtonLink href='/sign-in' variant='outline' size='lg' className='w-full' onClick={onClose}>
                    Sign in
                </ButtonLink>
            </div>
        </div>
    )
}
